const finderTagsContent = {
  title: "macOS Finder Tag Automations",
  description: "Read and write macOS Finder tags from Sortio Automations. Match files by tag, add or remove tags by rule, and assign Finder colors.",
  sections: [
    {
      id: "overview",
      title: "Overview",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          As of Sortio v8.11.0, the <a href="/docs/rules-engine" class="text-[#06c]">Automations</a> engine
          can both <strong>read</strong> macOS Finder tags as a condition and <strong>write</strong> them as
          an action. That means you can build deterministic rules like <em>"if Finder tag is Receipt, move to
          ~/Documents/Receipts"</em> or <em>"if file size is greater than 10 MB, add Finder tag Large in red."</em>
        </p>

        <p class="text-[#1d1d1f] text-lg mb-6">
          Finder tags are a native macOS feature, so this entire flow is <strong>macOS only</strong>. On
          Windows and Linux these conditions and actions are not available.
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="border border-[#e0e0e5] rounded-xl p-5 bg-white/80 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Match on tags</h4>
            <p class="text-[#424245] text-sm">Pick the <code class="bg-[#f5f5f7] px-1 rounded">Finder tag</code> field in any condition. Use comparators like <em>is</em>, <em>is any of</em>, or <em>has any tag</em> to gate a rule on what's already tagged.</p>
          </div>
          <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
            <h4 class="font-semibold text-[#1d1d1f] mb-2">Apply tags as an action</h4>
            <p class="text-[#424245] text-sm">The <code class="bg-[#f5f5f7] px-1 rounded">TAG</code> action takes a list of tag names, an optional Finder color, and a mode (Add, Remove, or Replace).</p>
          </div>
        </div>
      `
    },
    {
      id: "tag-conditions",
      title: "Finder Tag Conditions",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          When you build a condition in the Rule Builder, choose <strong>Finder tag</strong> from the field
          dropdown. Sortio reads the file's existing tags from macOS and compares them against the value or
          list you provide.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
            <h4 class="font-semibold text-[#1d1d1f]">Available comparators</h4>
          </div>
          <div class="p-4">
            <table class="w-full text-sm">
              <tbody class="text-[#424245]">
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-2 font-mono text-[#06c] w-44">is</td>
                  <td class="py-2">File has exactly this tag</td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-2 font-mono text-[#06c]">is not</td>
                  <td class="py-2">File does not have this tag</td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-2 font-mono text-[#06c]">contains</td>
                  <td class="py-2">A tag on the file contains this substring (case-insensitive)</td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-2 font-mono text-[#06c]">is any of</td>
                  <td class="py-2">File has at least one tag from a comma-separated list</td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-2 font-mono text-[#06c]">is none of</td>
                  <td class="py-2">File has none of the tags in the provided list</td>
                </tr>
                <tr class="border-b border-[#e0e0e5]">
                  <td class="py-2 font-mono text-[#06c]">has any tag</td>
                  <td class="py-2">File has at least one tag of any kind</td>
                </tr>
                <tr>
                  <td class="py-2 font-mono text-[#06c]">has no tags</td>
                  <td class="py-2">File has no tags at all</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="border border-[#06c]/20 rounded-xl p-5 bg-[#06c]/5 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] mb-2">Combine with other fields</h4>
          <p class="text-[#424245] text-sm">
            Tag conditions are first-class, so you can mix them freely with name, extension, size, dates, and
            anything else the Rule Builder supports. For example: <em>extension is pdf <strong>AND</strong>
            Finder tag is any of [Receipt, Invoice]</em>.
          </p>
        </div>
      `
    },
    {
      id: "tag-action",
      title: "The TAG Action",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Add a <strong>TAG</strong> action to any rule when you want to write tags onto matching files.
          The action has three inputs: the list of tags, an optional Finder color, and a mode.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Tags</h4>
              <p class="text-[#424245]">A comma-separated list of tag names. Existing tags with the same names are reused; new tag names create new tags in macOS.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Color (optional)</h4>
              <p class="text-[#424245] mb-2">Pick a Finder color. The color applies to the tags being written by this action.</p>
              <div class="flex flex-wrap gap-2 text-xs">
                <span class="px-2 py-1 rounded-full" style="background:#8e8e93;color:white">Gray</span>
                <span class="px-2 py-1 rounded-full" style="background:#34c759;color:white">Green</span>
                <span class="px-2 py-1 rounded-full" style="background:#af52de;color:white">Purple</span>
                <span class="px-2 py-1 rounded-full" style="background:#007aff;color:white">Blue</span>
                <span class="px-2 py-1 rounded-full" style="background:#ffcc00;color:#1d1d1f">Yellow</span>
                <span class="px-2 py-1 rounded-full" style="background:#ff3b30;color:white">Red</span>
                <span class="px-2 py-1 rounded-full" style="background:#ff9500;color:white">Orange</span>
              </div>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Mode</h4>
              <ul class="list-disc list-inside text-[#424245] space-y-1 ml-2">
                <li><strong>Add</strong>: append the listed tags to whatever the file already has. Existing tags are preserved.</li>
                <li><strong>Remove</strong>: drop the listed tags from the file. Tags not in the list are untouched.</li>
                <li><strong>Replace</strong>: overwrite the file's entire tag set with exactly the tags in the list.</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="border border-amber-100 rounded-xl p-5 bg-amber-50/80 backdrop-blur">
          <h4 class="font-semibold text-[#1d1d1f] flex items-center mb-2">
            <span class="mr-2">ℹ️</span>
            Replace is destructive
          </h4>
          <p class="text-[#424245] text-sm">Replace mode wipes any tag not present in your action's tag list. If you only want to <em>add</em>, leave the mode on Add (the default).</p>
        </div>
      `
    },
    {
      id: "example-receipts",
      title: "Example: Route Receipts by Tag",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          A common workflow is to tag receipts in Finder as you save them, then let Sortio file them
          automatically. This rule watches for any file tagged "Receipt" and moves it to a year-based
          folder under your Documents directory.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
            <h4 class="font-semibold text-[#1d1d1f]">Receipt routing rule</h4>
          </div>
          <div class="p-4">
            <code class="block bg-[#1d1d1f] text-green-400 p-3 rounded-lg text-xs font-mono">
Conditions: ALL OF<br/>
&nbsp;&nbsp;- Finder tag is "Receipt"<br/><br/>
Actions:<br/>
&nbsp;&nbsp;1. MOVE → ~/Documents/Receipts
            </code>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg mb-6">
          To go further, pair this with a TAG action that adds a "Filed" tag in green so you can see at a
          glance which receipts Sortio has already routed.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-6">
          <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
            <h4 class="font-semibold text-[#1d1d1f]">Receipt routing + visual confirmation</h4>
          </div>
          <div class="p-4">
            <code class="block bg-[#1d1d1f] text-green-400 p-3 rounded-lg text-xs font-mono">
Conditions: ALL OF<br/>
&nbsp;&nbsp;- Finder tag is "Receipt"<br/><br/>
Actions:<br/>
&nbsp;&nbsp;1. MOVE → ~/Documents/Receipts<br/>
&nbsp;&nbsp;2. TAG → ["Filed"], color: Green, mode: Add
            </code>
          </div>
        </div>
      `
    },
    {
      id: "example-large-files",
      title: "Example: Flag Large Files in Red",
      content: `
        <p class="text-[#1d1d1f] text-lg mb-6">
          Tags are also useful as visual flags. This rule adds a red "Large" tag to any file over 10 MB so
          they stand out when you're cleaning out a Downloads folder.
        </p>

        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="border-b border-[#e0e0e5] p-4 bg-[#f5f5f7]">
            <h4 class="font-semibold text-[#1d1d1f]">Flag large files</h4>
          </div>
          <div class="p-4">
            <code class="block bg-[#1d1d1f] text-green-400 p-3 rounded-lg text-xs font-mono">
Conditions: ALL OF<br/>
&nbsp;&nbsp;- size GT 10485760 (10 MB)<br/>
&nbsp;&nbsp;- Finder tag is none of ["Large"]<br/><br/>
Actions:<br/>
&nbsp;&nbsp;1. TAG → ["Large"], color: Red, mode: Add
            </code>
          </div>
        </div>

        <p class="text-[#1d1d1f] text-lg mt-6">
          The <em>is none of</em> guard prevents the rule from re-running on files that are already tagged
          Large. If you're using "Stop on match" semantics in your rule list, that gate is optional.
        </p>
      `
    },
    {
      id: "tips",
      title: "Tips & Caveats",
      content: `
        <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden">
          <div class="p-6 space-y-5">
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">macOS only</h4>
              <p class="text-[#424245]">Finder tags are a macOS feature. On Windows or Linux, the <em>Finder tag</em> condition and the TAG action are not exposed.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Tag names are case-sensitive in macOS, lookup is forgiving</h4>
              <p class="text-[#424245]">macOS treats "Receipt" and "receipt" as separate tags, but Sortio's <em>contains</em> comparator matches case-insensitively. Use <em>is</em> when you need an exact match and <em>contains</em> when you don't.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Combine with Spaces auto-tag</h4>
              <p class="text-[#424245]">If you want tags applied <em>and then</em> routed by rule, use <a href="/docs/auto-tag-spaces" class="text-[#06c]">Auto-tag in Spaces</a> to populate the tags, and Finder-tag-aware Automations to act on them.</p>
            </div>
            <div>
              <h4 class="font-semibold text-[#1d1d1f] mb-2">Dry-run first</h4>
              <p class="text-[#424245]">Tagging is fast and reversible, but writing tags to a wide condition can produce noisy results. Always preview a new rule with dry-run before enabling it.</p>
            </div>
          </div>
        </div>
      `
    }
  ]
};

export default finderTagsContent;
